import React, { useState } from 'react';
import { explainChemistryConcept } from '../services/geminiService';
import type { WaterBodyType } from '../types';

const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center p-4">
    <svg className="animate-spin h-5 w-5 text-cyan-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
    <span className="ml-3">Thinking...</span>
  </div>
);

const POOL_SECTIONS = [
  {
    title: 'Chlorine (FC & CC)',
    content:
      "Free Chlorine (FC) is the active sanitizer that kills germs and algae. Combined Chlorine (CC), or chloramines, is the 'used-up' chlorine that creates the classic 'pool smell' and irritates eyes. Too little FC allows germs to multiply. High CC means the water is full of contaminants and the sanitizer is ineffective. Ideal Range: FC should be 2–4 ppm (or 7.5% of your CYA level). CC should always be below 0.5 ppm.",
  },
  {
    title: 'pH & Acid/Base Demand',
    content:
      "pH is a measure of how acidic or basic your water is. It is critical for sanitizer effectiveness and swimmer comfort. If pH is too high, chlorine becomes sluggish. If it's too low, the water becomes acidic and can damage pool equipment. The Acid/Base Demand test tells you exactly how much acid or base to add to reach your target pH. Ideal Range: 7.4 to 7.6.",
  },
  {
    title: 'Total Alkalinity (TA)',
    content:
      "A measure of dissolved alkaline substances in the water. Think of it as a 'buffer' or 'anchor' for your pH. Proper TA levels prevent the pH from swinging wildly. If TA is too low, your pH will be unstable. If TA is too high, the pH will be very difficult to change. Ideal Range: 80 to 120 ppm.",
  },
  {
    title: 'Calcium Hardness (CH)',
    content:
      'The amount of dissolved calcium in the pool water. If CH is too low, the water will try to get calcium from anywhere it can, corroding plaster surfaces and equipment. If it is too high, it can cause cloudy water and scaly deposits. Ideal Range: 200 to 400 ppm.',
  },
  {
    title: 'Cyanuric Acid (CYA)',
    content:
      "Often called 'stabilizer' or 'conditioner'. It acts like a sunscreen for your chlorine. Without CYA, sunlight can destroy most of your free chlorine in hours. CYA protects it, but too much CYA can make your chlorine slow and ineffective. Ideal Range: 30 to 50 ppm for a typical chlorinated pool.",
  },
  {
    title: 'Total Bromine',
    content:
      'Bromine is a sanitizer, similar to chlorine, often used in spas and hot tubs, but also an option for pools. It is more stable at higher temperatures and in a wider pH range than chlorine. Total Bromine is the measure of both active (free) and used-up (combined) bromine. Ideal Range: 2–6 ppm for pools.',
  },
];

const HOT_TUB_SECTIONS = [
  {
    title: '♨️ Hot Tub vs. Pool Chemistry',
    content:
      'Hot tubs present unique challenges: small volume (200–600 gal) means small chemical changes have huge concentration impacts. High temperature (100–104°F) accelerates chemical reactions, degrades chlorine faster, and drives pH upward. Heavy bather load relative to water volume demands more frequent testing and adjustment than a pool.',
  },
  {
    title: '🟫 Bromine — The Preferred Spa Sanitizer',
    content:
      'Bromine is more stable than chlorine at high temps, making it ideal for hot tubs. It works over a broader pH range (7.0–8.0) and produces fewer irritating byproducts. A "bromine bank" builds up in the water — use MPS shock to reactivate spent bromamines into active sanitizer. Ideal Range: 3–5 ppm.',
  },
  {
    title: '⚡ Non-Chlorine Shock (MPS)',
    content:
      'Potassium monopersulfate (MPS) is the go-to oxidizer for hot tubs. Unlike chlorine shock, MPS oxidizes contaminants without raising sanitizer levels, allows re-entry in just 15–30 minutes, and reactivates bromine. Use after every soak or 2–3× per week.',
  },
  {
    title: '⬆️ pH Drift in Hot Tubs',
    content:
      'Hot tub pH constantly drifts upward due to aeration from jets and off-gassing of CO₂. Test pH every 2–3 days. Use dry acid (sodium bisulfate) or muriatic acid to lower it. Ideal target: 7.2–7.6.',
  },
  {
    title: '🔄 The Drain & Refill Rule',
    content:
      "Water in a hot tub gets saturated with Total Dissolved Solids (TDS) much faster than a pool. Target a full drain & refill every 3–4 months. Quick calculation: Hot Tub Volume ÷ (Daily Bathers × 3) = Days between drains.",
  },
  {
    title: '🫧 Foam & Biofilm',
    content:
      'Foam is caused by body oils, lotions, shampoos, and detergent residue from bathing suits. Use an enzyme product weekly to break down organics. Persistent foam usually means it is time for a drain & refill or a plumbing line flush.',
  },
  {
    title: '💎 Calcium Hardness in Hot Tubs',
    content:
      'Acrylic shells tolerate lower CH (150–250 ppm). However, very soft water (below 100 ppm) is corrosive and attacks heater elements, jets, and pump seals. High CH (above 300 ppm) can clog jets and coat the heater with scale.',
  },
  {
    title: '🚿 Spa Line Flush Protocol',
    content:
      'Before every drain & refill, run a jet-line purge product (like Ahh-Some) with jets on high for 20–30 minutes. This dislodges biofilm colonies inside the plumbing — invisible biofilm causes chronic contamination, odors, and high sanitizer demand.',
  },
  {
    title: '🧪 Chlorine (FC & CC)',
    content:
      "Free Chlorine (FC) is the active sanitizer that kills germs and algae. Combined Chlorine (CC) signals exhausted chlorine. In hot tubs using chlorine, target FC 3–5 ppm. If using bromine as the primary sanitizer, FC may read 0 — that is normal.",
  },
  {
    title: '⚗️ pH & Acid/Base Demand',
    content:
      'pH controls sanitizer effectiveness and bather comfort. Hot tubs drift high faster than pools due to aeration. Ideal target is 7.2–7.6. The acid demand test tells you exactly how much acid to add.',
  },
  {
    title: '🧂 Total Alkalinity (TA)',
    content:
      "TA is the buffer that stabilizes pH. In spas, target 80–120 ppm. Low TA causes pH to swing wildly. High TA makes pH nearly impossible to lower.",
  },
];

interface DetailsWithAIProps {
  title: string;
  content: string;
}

const DetailsWithAI: React.FC<DetailsWithAIProps> = ({ title, content }) => {
  const [aiExplanation, setAiExplanation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleExplain = async () => {
    setIsLoading(true);
    setError('');
    setAiExplanation('');
    try {
      const explanation = await explainChemistryConcept(title, content);
      setAiExplanation(explanation);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unknown error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <details className="bg-slate-900/50 rounded-lg p-3 group">
      <summary className="cursor-pointer flex justify-between items-center font-semibold text-lg text-white list-none">
        {title}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-5 w-5 text-cyan-400 transition-transform duration-200 group-open:rotate-90"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </summary>
      <div className="mt-3 pt-3 border-t border-slate-700 text-slate-300 text-sm space-y-3">
        <p>{content}</p>
        <button
          onClick={handleExplain}
          disabled={isLoading}
          className="text-sm bg-cyan-700/50 hover:bg-cyan-600/50 text-cyan-200 font-semibold py-1 px-3 rounded-md transition-colors duration-200 disabled:bg-slate-600 disabled:cursor-not-allowed"
        >
          Expand with AI
        </button>
        {isLoading && <LoadingSpinner />}
        {error && <div className="mt-2 bg-red-900/50 text-red-300 p-2 rounded-md text-xs">{error}</div>}
        {aiExplanation && (
          <div className="mt-2 bg-slate-800 p-3 rounded-lg space-y-2 whitespace-pre-wrap font-sans text-slate-200">
            {aiExplanation}
          </div>
        )}
      </div>
    </details>
  );
};

interface Chem101TabProps {
  waterBody?: WaterBodyType;
}

export const Chem101Tab: React.FC<Chem101TabProps> = ({ waterBody = 'pool' }) => {
  const isHotTub = waterBody === 'hot_tub';
  const sections = isHotTub ? HOT_TUB_SECTIONS : POOL_SECTIONS;

  return (
    <div className="bg-slate-800 p-4 rounded-lg shadow-md animate-fade-in">
      <h2 className="text-2xl font-bold text-cyan-400 text-center mb-1">
        {isHotTub ? '♨️ Spa & Hot Tub Chemistry 101' : 'Pool Chemistry 101'}
      </h2>
      {isHotTub && (
        <p className="text-center text-orange-400 text-sm mb-4">
          Ranges and concepts calibrated for hot tub / spa conditions
        </p>
      )}
      {!isHotTub && <p className="text-center text-slate-400 text-sm mb-4">&nbsp;</p>}
      <div className="space-y-3">
        {sections.map((section) => (
          <DetailsWithAI key={section.title} title={section.title} content={section.content} />
        ))}
      </div>
    </div>
  );
};